import { ChevronRight } from "lucide-react"
import * as React from "react"

import { useLocale, useT } from "@/i18n/provider"
import { cn } from "@/lib/utils"

type Container = Record<string, unknown> | unknown[]

//: Strings longer than this start clipped, with a toggle to read the rest.
const STRING_CLIP = 300
//: A collapsed node previews its contents until the preview reaches this many characters.
const PREVIEW_BUDGET = 60

function isContainer(value: unknown): value is Container {
  return value !== null && typeof value === "object"
}

function entriesOf(value: Container): [string, unknown][] {
  return Array.isArray(value)
    ? value.map((item, index) => [String(index), item])
    : Object.entries(value)
}

/** A string holding a JSON object or array, parsed; anything else comes back undefined. */
export function parseJsonContainer(text: string | null | undefined): Container | undefined {
  const trimmed = text?.trim()
  if (!trimmed || !(trimmed.startsWith("{") || trimmed.startsWith("["))) return undefined
  try {
    const value: unknown = JSON.parse(trimmed)
    return isContainer(value) ? value : undefined
  } catch {
    return undefined
  }
}

function shortValue(value: unknown): string {
  if (Array.isArray(value)) return value.length ? "[…]" : "[]"
  if (isContainer(value)) return Object.keys(value).length ? "{…}" : "{}"
  if (typeof value === "string") {
    const oneLine = value.replace(/\s+/g, " ")
    return JSON.stringify(oneLine.length > 24 ? `${oneLine.slice(0, 24)}…` : oneLine)
  }
  return String(value)
}

function preview(value: Container): string {
  const isArray = Array.isArray(value)
  const parts: string[] = []
  let length = 0
  for (const [key, item] of entriesOf(value)) {
    if (length > PREVIEW_BUDGET) {
      parts.push("…")
      break
    }
    const part = isArray ? shortValue(item) : `${key}: ${shortValue(item)}`
    parts.push(part)
    length += part.length
  }
  return parts.join(", ")
}

function countLabel(value: Container, t: ReturnType<typeof useT>): string {
  const count = entriesOf(value).length
  return t(Array.isArray(value) ? "common.itemCount" : "common.keyCount", { count })
}

/**
 * JSON as a tree: objects and arrays fold open and closed, collapsed ones show a
 * one-line preview. Levels shallower than `defaultExpandDepth` start open.
 */
export function JsonViewer({
  value,
  defaultExpandDepth = 1,
  className,
}: {
  value: unknown
  defaultExpandDepth?: number
  className?: string
}) {
  const t = useT()
  // Remounting the tree under a new key is what resets every node for expand/collapse all.
  const [view, setView] = React.useState({ depth: defaultExpandDepth, generation: 0 })
  const foldable = isContainer(value) && Object.values(value).some(isContainer)

  return (
    <div className={cn("payload bg-muted/50 flex flex-col overflow-hidden rounded-md", className)}>
      {foldable && (
        <div className="text-muted-foreground flex justify-end gap-3 px-3 pt-2 font-sans text-[11px]">
          <button
            type="button"
            className="hover:text-foreground"
            onClick={() => setView((v) => ({ depth: Infinity, generation: v.generation + 1 }))}
          >
            {t("common.expandAll")}
          </button>
          <button
            type="button"
            className="hover:text-foreground"
            onClick={() => setView((v) => ({ depth: 1, generation: v.generation + 1 }))}
          >
            {t("common.collapseAll")}
          </button>
        </div>
      )}
      <div className={cn("min-h-0 overflow-auto p-3", foldable && "pt-1")}>
        <JsonNode key={view.generation} value={value} depth={0} expandDepth={view.depth} last />
      </div>
    </div>
  )
}

/** Tool output is text that is often JSON: a tree when it parses, verbatim otherwise. */
export function JsonOrText({
  text,
  defaultExpandDepth,
  className,
}: {
  text: string
  defaultExpandDepth?: number
  className?: string
}) {
  const parsed = React.useMemo(() => parseJsonContainer(text), [text])
  if (parsed === undefined) {
    return (
      <pre
        className={cn(
          "payload bg-muted/50 overflow-auto rounded-md p-3 whitespace-pre-wrap",
          className,
        )}
      >
        {text}
      </pre>
    )
  }
  return <JsonViewer value={parsed} defaultExpandDepth={defaultExpandDepth} className={className} />
}

function JsonNode({
  name,
  value,
  depth,
  expandDepth,
  last,
  isIndex = false,
}: {
  name?: string
  value: unknown
  depth: number
  expandDepth: number
  last: boolean
  isIndex?: boolean
}) {
  const t = useT()
  const [open, setOpen] = React.useState(depth < expandDepth)
  const comma = last ? null : <span className="text-muted-foreground">,</span>
  const label =
    name === undefined ? null : (
      <>
        <span className={isIndex ? "text-muted-foreground" : "text-sky-700 dark:text-sky-300"}>
          {name}
        </span>
        <span className="text-muted-foreground">: </span>
      </>
    )

  if (!isContainer(value) || entriesOf(value).length === 0) {
    return (
      <div className="pl-4 break-words">
        {label}
        <JsonPrimitive value={value} />
        {comma}
      </div>
    )
  }

  const isArray = Array.isArray(value)
  const [openBracket, closeBracket] = isArray ? ["[", "]"] : ["{", "}"]
  const entries = entriesOf(value)

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="hover:bg-foreground/5 relative block w-full rounded-sm pl-4 text-left"
      >
        <ChevronRight
          className={cn(
            "text-muted-foreground absolute top-[0.3em] left-0.5 size-3 transition-transform",
            open && "rotate-90",
          )}
        />
        {label}
        <span className="text-muted-foreground">{openBracket}</span>
        {!open && (
          <>
            <span className="text-muted-foreground/80">{preview(value)}</span>
            <span className="text-muted-foreground">{closeBracket}</span>
            {comma}
            <span className="text-muted-foreground/70 ml-2 font-sans text-[10px]">
              {countLabel(value, t)}
            </span>
          </>
        )}
      </button>
      {open && (
        <>
          <div className="border-border ml-[7px] border-l pl-2">
            {entries.map(([key, item], index) => (
              <JsonNode
                key={key}
                name={key}
                value={item}
                depth={depth + 1}
                expandDepth={expandDepth}
                last={index === entries.length - 1}
                isIndex={isArray}
              />
            ))}
          </div>
          <div className="pl-4">
            <span className="text-muted-foreground">{closeBracket}</span>
            {comma}
          </div>
        </>
      )}
    </div>
  )
}

function JsonPrimitive({ value }: { value: unknown }) {
  if (typeof value === "string") return <JsonString value={value} />
  if (typeof value === "number") {
    return <span className="text-amber-700 dark:text-amber-400">{value}</span>
  }
  if (typeof value === "boolean") {
    return <span className="text-violet-700 dark:text-violet-400">{String(value)}</span>
  }
  if (Array.isArray(value)) return <span className="text-muted-foreground">[]</span>
  if (isContainer(value)) return <span className="text-muted-foreground">{"{}"}</span>
  return <span className="text-muted-foreground italic">null</span>
}

function JsonString({ value }: { value: string }) {
  const t = useT()
  const { locale } = useLocale()
  const [full, setFull] = React.useState(false)
  const clipped = !full && value.length > STRING_CLIP
  return (
    // Real line breaks instead of \n escapes: prompts and tool output stay readable.
    <span className="whitespace-pre-wrap text-emerald-700 dark:text-emerald-400">
      "{clipped ? value.slice(0, STRING_CLIP) : value}
      {clipped && "…"}"
      {value.length > STRING_CLIP && (
        <button
          type="button"
          onClick={() => setFull((f) => !f)}
          className="text-muted-foreground hover:text-foreground ml-2 font-sans text-[10px] underline underline-offset-2"
        >
          {full
            ? t("common.showLess")
            : t("common.showAllChars", { count: value.length.toLocaleString(locale) })}
        </button>
      )}
    </span>
  )
}
